import React, { useState, useEffect, useRef, useCallback } from "react";
import Select, { components } from "react-select";
import { Controller } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { getNameProfile } from "Utils/utils";
import { IMAGE_URL } from "store/services/URL";
import useCareLabels from "hooks/useCareLabels";

/**
 * Async Multi-Select Dropdown with infinite scroll and search
 * Designed for large datasets with server-side filtering and pagination
 */
const AsyncMultiSelectDropdown = ({
    options = [],
    isLoading = false,
    hasMore = false,
    onInputChange,
    onMenuScrollToBottom,
    onMenuOpen,
    control,
    name,
    setValue,
    watch,
    clearErrors,
    showIcon = false,
    isDisabled = false,
    errorMessage,
    rules,
    cssClass = "",
    loadingPlaceholder: loadingPlaceholderProp,
    noOptionsPlaceholder: noOptionsPlaceholderProp,
    noOptionsSearchPlaceholder: noOptionsSearchPlaceholderProp,
    placeholder: placeholderProp,
    menuPortalZIndex = 10001,
    minSelectionCount = 0,
    onMinSelectionBlocked,
    /**
     * When true, close the portaled menu if the page/modal scrolls.
     * Scrolling inside the options list stays open. Opt-in so other screens are unchanged.
     */
    closeMenuOnPageScroll = false,
    /** Optional override. When provided, used instead of closeMenuOnPageScroll. */
    closeMenuOnScroll: closeMenuOnScrollProp,
    /** Optional react-select menuPosition. Omitted unless the caller passes it. */
    menuPosition,
    /**
     * Opt-in: filter the already-loaded options in the browser.
     * Default stays server-side (filterOption always true + onInputChange search).
     */
    clientSideFilter = false,
}) => {
    const { participantLabelPlural } = useCareLabels();
    const loadingPlaceholder =
      loadingPlaceholderProp ?? `Loading ${participantLabelPlural.toLowerCase()}...`;
    const noOptionsPlaceholder =
      noOptionsPlaceholderProp ??
      `No ${participantLabelPlural.toLowerCase()} available`;
    const noOptionsSearchPlaceholder =
      noOptionsSearchPlaceholderProp ??
      ((q) => `No ${participantLabelPlural.toLowerCase()} found for "${q}"`);
    const placeholder =
      placeholderProp ?? `Search ${participantLabelPlural.toLowerCase()}...`;

    const [menuIsOpen, setMenuIsOpen] = useState(undefined);
    const loadMoreRef = useRef(null);
    const observerRef = useRef(null);

    // Force menu to stay open during loading if already open
    useEffect(() => {
        if (isLoading && menuIsOpen === true) {
            // Keep menu open during loading
            setMenuIsOpen(true);
        }
    }, [isLoading, menuIsOpen]);

    // Stable callback for infinite scroll
    const handleLoadMore = useCallback(() => {
        if (hasMore && !isLoading && onMenuScrollToBottom) {
            onMenuScrollToBottom();
        }
    }, [hasMore, isLoading, onMenuScrollToBottom]);

    useEffect(() => {
        if (!loadMoreRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry?.isIntersecting) {
                    handleLoadMore();
                }
            },
            {
                threshold: 0.1,
                rootMargin: '50px',
            }
        );

        observer.observe(loadMoreRef.current);
        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleLoadMore, menuIsOpen]);

    /**
     * Custom loading message component.
     * Show loading message for initial/search (no options yet).
     * When options exist, loading is for "load more" – show nothing here to avoid duplicate loader with MenuList.
     */
    const LoadingMessage = () => {
        if (options.length > 0) return null;
        return (
            <div className="flex items-center justify-center py-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                <span className="ml-2 text-sm text-gray-600">
                    {loadingPlaceholder}
                </span>
            </div>
        );
    };

    /**
     * Custom no options message
     */
    const NoOptionsMessage = ({ inputValue }) => (
        <div className="text-center py-3 text-sm text-gray-500">
            {inputValue
                ? (typeof noOptionsSearchPlaceholder === 'function' ? noOptionsSearchPlaceholder(inputValue) : `No ${participantLabelPlural.toLowerCase()} found for "${inputValue}"`)
                : noOptionsPlaceholder}
        </div>
    );

    /**
     * Custom menu list with load more indicator at bottom
     */
    const MenuList = (props) => {
        return (
            <components.MenuList {...props}>
                {props.children}
                {/* Load more indicator - always render for IntersectionObserver */}
                {hasMore && (
                    <div
                        ref={loadMoreRef}
                        className="text-center py-2 text-xs text-gray-400 border-t border-gray-100"
                        style={{ display: isLoading ? 'none' : 'block' }}
                    >
                        Scroll for more...
                    </div>
                )}
                {/* Loading more indicator – only when loading next page (options already present), not during initial/search */}
                {isLoading && hasMore && options.length > 0 && (
                    <div className="flex items-center justify-center py-2 border-t border-gray-200">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                        <span className="ml-2 text-xs text-gray-500">Loading more...</span>
                    </div>
                )}
            </components.MenuList>
        );
    };



    const resolveNameParts = (data) => {
        const first = data?.first_name?.trim() || "";
        const last = data?.last_name?.trim() || "";
        if (first && last) return { first, last };

        const parts = String(data?.label || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (parts.length >= 2) {
            return { first: parts[0], last: parts[parts.length - 1] };
        }
        if (parts.length === 1) {
            return { first: parts[0], last: parts[0] };
        }
        return { first: "?", last: "?" };
    };

    /**
     * Profile photo, icon, or initials avatar for selected tags and options.
     */
    const getAvatarSrc = (data) => {
        if (data?.icon) {
            const icon = String(data.icon).trim();
            if (icon.startsWith("http")) return icon;
            return `${IMAGE_URL}${icon}`;
        }

        const photo = data?.photo ? String(data.photo).trim() : "";
        if (photo !== "") {
            return photo.startsWith("http") ? photo : `${IMAGE_URL}${photo}`;
        }

        const pic = data?.profile_picture ? String(data.profile_picture).trim() : "";
        if (pic !== "") {
            return pic.startsWith("http") ? pic : `${IMAGE_URL}${pic}`;
        }

        const { first, last } = resolveNameParts(data);
        return getNameProfile(first, last) || getNameProfile("?", "?");
    };

    const handleImageError = (e, data) => {
        const { first, last } = resolveNameParts(data);
        e.target.src = getNameProfile(first, last) || getNameProfile("?", "?");
    };

    /**
     * Custom option – profile_picture with IMAGE_URL or name avatar
     */
    const CustomOption = (props) => {
        const { data } = props;
        return (
            <components.Option {...props}>
                <div className="flex items-center capitalize">
                    {data?.label && (
                        <img
                            className="w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2"
                            src={getAvatarSrc(data)}
                            alt=""
                            onError={(e) => handleImageError(e, data)}
                        />
                    )}
                    {data?.label}
                </div>
            </components.Option>
        );
    };

    /**
     * Custom single value – profile_picture with IMAGE_URL or name avatar
     */
    const CustomSingleValue = (csprops) => {
        const data = csprops.data;
        return (
            <components.SingleValue {...csprops}>
                <div className="flex items-center capitalize">
                    {data?.label && (
                        <img
                            className="w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2"
                            src={getAvatarSrc(data)}
                            alt=""
                            onError={(e) => handleImageError(e, data)}
                        />
                    )}
                    {data?.label}
                </div>
            </components.SingleValue>
        );
    };

    /**
     * Custom multi-value label – profile_picture with IMAGE_URL or name avatar
     */
    const CustomMultiValueLabel = (props) => {
        const { data } = props;
        return (
            <components.MultiValueLabel {...props}>
                <div className="flex items-center capitalize">
                    {data?.label && (
                        <img
                            className="w-5 h-5 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-1"
                            src={getAvatarSrc(data)}
                            alt=""
                            onError={(e) => handleImageError(e, data)}
                        />
                    )}
                    <span className="truncate">{data?.label}</span>
                </div>
            </components.MultiValueLabel>
        );
    };

    /**
     * Custom styles
     */
    const customStyles = {
        multiValueRemove: (base, state) => ({
            ...base,
            display: "block",
        }),
        menuPortal: (base) => ({ ...base, zIndex: menuPortalZIndex }),
        menuList: (base) => ({
            ...base,
            maxHeight: "250px !important",
            overflowY: "auto !important",
            scrollbarWidth: "thin",
        }),
        valueContainer: (base) => ({
            ...base,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            maxHeight: "100%",
            overflowY: "auto",
        }),
        multiValue: (base) => ({
            ...base,
            margin: "2px",
        }),
        control: (base) => ({
            ...base,
            minHeight: "40px",
            alignItems: "flex-start",
        }),
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            rules={rules}
            render={({ field }) => (
                <>
                    <Select
                        {...field}
                        isMulti
                        isDisabled={isDisabled}
                        options={options}
                        value={watch(name) || []}
                        closeMenuOnSelect={false}
                        isLoading={isLoading}
                        onInputChange={(inputValue, { action }) => {
                            if (clientSideFilter) return;
                            // Only trigger search on user input, not on other actions
                            if (action === "input-change" && onInputChange) {
                                onInputChange(inputValue);
                            }
                        }}
                        {...(clientSideFilter
                            ? {}
                            : { filterOption: () => true })}
                        onChange={(selectedOptions) => {
                            const next = selectedOptions || [];
                            if (minSelectionCount > 0 && next.length < minSelectionCount) {
                                onMinSelectionBlocked?.();
                                return;
                            }
                            setValue(name, next);
                            clearErrors(name);
                        }}
                        isClearable={!minSelectionCount}
                        components={{
                            MenuList,
                            LoadingMessage,
                            NoOptionsMessage,
                            ...(showIcon
                                ? {
                                    Option: CustomOption,
                                    SingleValue: CustomSingleValue,
                                    MultiValueLabel: CustomMultiValueLabel,
                                }
                                : {}),
                        }}
                        styles={customStyles}
                        // menuPortalTarget={document.body} // Commented out to fix scroll detection
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={() => {
                            setMenuIsOpen(true);
                            if (onMenuOpen) onMenuOpen();
                        }}
                        onMenuClose={() => setMenuIsOpen(undefined)}
                        classNamePrefix='async-multi-select-dropdown'
                        className={`basic-multi-select w-100 roleSelector scrollOptions ${cssClass}`}
                        placeholder={placeholder}
                        menuPortalTarget={menuPortalZIndex > 10000 ? document.body : undefined}
                        {...(menuPosition ? { menuPosition } : {})}
                        closeMenuOnScroll={
                            typeof closeMenuOnScrollProp === "function"
                                ? closeMenuOnScrollProp
                                : closeMenuOnPageScroll
                                ? (event) => {
                                      const target = event?.target;
                                      if (
                                          target instanceof Element &&
                                          target.closest(
                                              ".async-multi-select-dropdown__menu"
                                          )
                                      ) {
                                          return false;
                                      }
                                      return true;
                                  }
                                : undefined
                        }
                    />
                    {errorMessage && <p className="text-red-500 text-xs mt-1">{errorMessage}</p>}
                </>
            )}
        />
    );
};

export default AsyncMultiSelectDropdown;
